import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MyDepartmentsNav from './department/MyDepartmentsNav';
import useNavItems, { useIconRail } from './navItems';

/**
 * Destinations come from useNavItems (shared with the TopBar's phone menu, so
 * the two can't drift). On the 601-900px rail only the icon shows: the clipped
 * label gives a screen reader the name, and a native `title` gives a sighted
 * user one. Collapsed on desktop, the CSS flyout names the item. With the full sidebar
 * the label is visible, so no title (it would only repeat it).
 */
export default function Sidebar({ collapsed, onToggle }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { home, items, isDeptManager } = useNavItems();
  const iconRail = useIconRail();
  // Collapsed on desktop has its own hover/focus flyout (a title there showed
  // twice). At 601-900px the rail's label clip also hides that flyout, so the
  // rail gets a title whether or not it is collapsed.
  const titled = iconRail;

  const link = ({ key, to, label, Icon, isActive }) => {
    const active = isActive(location.pathname);
    return (
      <a
        key={key}
        href={to}
        title={titled && label ? label : undefined}
        aria-current={active ? 'page' : undefined}
        className={`sidebar-nav-item ${active ? 'active' : ''}`}
        onClick={(e) => { e.preventDefault(); navigate(to); }}
      >
        <Icon />
        {label
          ? <span className="sidebar-nav-label">{label}</span>
          // Home before the flag map loads: a neutral bar, never a guess.
          : <span className="sidebar-nav-label sidebar-nav-label--pending" aria-hidden="true" />}
      </a>
    );
  };

  return (
    <aside className={`app-sidebar ${collapsed ? 'app-sidebar--collapsed' : ''}`}>
      <div className="sidebar-inner">
        <div className="sidebar-brand">
          <img src="/images/rtg-logo-red.png" alt="RTG" className="sidebar-brand-logo-img" />
          <span className="sidebar-brand-text">
            <span className="sidebar-brand-name">Nexus</span>
            <span className="sidebar-brand-subtitle">Employee</span>
          </span>
        </div>

        <nav className="sidebar-nav" aria-label="Main navigation">
          {link({ key: 'home', ...home })}
          {items.map(link)}
          {/* Only dept managers: the server 403s everyone else on /api/dept/managed. */}
          {isDeptManager && <MyDepartmentsNav labelsHidden={titled} iconOnly={titled || collapsed} />}
        </nav>

        <button
          type="button"
          className="sidebar-toggle"
          onClick={onToggle}
          aria-label={collapsed ? 'Show sidebar' : 'Hide sidebar'}
          title={collapsed ? 'Show sidebar' : 'Hide sidebar'}
        >
          {collapsed ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M9 18l6-6-6-6" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          )}
          {!collapsed && <span className="sidebar-toggle-label">Collapse</span>}
        </button>
      </div>
    </aside>
  );
}
